import React, { useRef, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { ScribeButton as Button } from '../../../components/scribe/ScribeUI';
import { supabase } from '../../../supabaseClient';

export default function CopyTemplateDialog({ source, userDetails, orgId, onClose, onCopied }) {
  const [title, setTitle] = useState(`${source.title} (COPY)`);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const busy = useRef(false);
  const initialScope = useRef({ userId: userDetails?.id, orgId });
  const scope = useRef({ userId: userDetails?.id, orgId });
  scope.current = { userId: userDetails?.id, orgId };
  const sameScope = () => scope.current.userId === initialScope.current.userId && scope.current.orgId === initialScope.current.orgId;
  const copy = async event => {
    event.preventDefault();
    if (busy.current || !title.trim() || !userDetails?.id || !sameScope()) return;
    busy.current = true; setSaving(true); setError('');
    try {
      // Read again at confirmation: a colleague may have edited the source since the list loaded.
      const { data: latest, error: readError } = await supabase.from('templates').select('id,userid,content').eq('id', source.id).single();
      if (readError || !latest || latest.userid !== source.userid) throw new Error('Source unavailable');
      const { data: content, error: decryptError } = await supabase.rpc('decrypt_text', { encrypted_data: latest.content });
      if (decryptError || typeof content !== 'string') throw new Error('Source could not be read');
      if (!sameScope()) return;
      const copyData = { title: title.trim(), content, userid: initialScope.current.userId,
        created_by: userDetails.name || '', is_base_template: false, default: null };
      const { data, error: insertError } = await supabase.from('templates').insert([copyData]).select('id,created_at,updated_at').single();
      if (insertError || !data?.id) throw new Error('Copy could not be saved');
      if (sameScope()) onCopied({ ...copyData, id: data.id, created_at: data.created_at, updated_at: data.updated_at });
    } catch {
      setError('Could not copy this template. Please try again.');
    } finally { busy.current = false; setSaving(false); }
  };
  return <Dialog open onClose={() => { if (!busy.current) onClose(); }} fullWidth maxWidth="sm" aria-labelledby="copy-template-title">
    <form onSubmit={copy}>
      <DialogTitle id="copy-template-title">Copy template</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create your own template to edit. Changes to your copy will not affect the original.</Typography>
        <TextField autoFocus required fullWidth label="Template name" value={title} disabled={saving} onChange={event => setTitle(event.target.value)} margin="dense" />
        {!sameScope() && <Alert severity="warning">Your account or organization changed. Close this dialog and try again.</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={saving}>Cancel</Button><Button type="submit" variant="contained" disabled={saving || !title.trim() || !sameScope()}>{saving ? 'Copying…' : 'Copy'}</Button></DialogActions>
    </form>
  </Dialog>;
}
